import NavCard from "@/components/NavCard";
import PositionTable from "@/components/PositionTable";
import OrderTicket from "@/components/OrderTicket";
import EquityCurveChart from "@/components/EquityCurveChart";
import PnlChart from "@/components/PnlChart";
import { usePositions } from "@/hooks/usePositions";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export default function Dashboard() {
  const { positions } = usePositions();
  const { data: orderData } = useQuery({
    queryKey: ["orders-recent"],
    queryFn: () => api.get<{ data: unknown[]; total: number }>("/orders?limit=1"),
  });

  // Compute summary from positions
  const totalInvested = positions.reduce((s, p) => s + (p.invested ?? 0), 0);
  const totalCurrent = positions.reduce((s, p) => s + (p.currentValue ?? 0), 0);
  const totalPnl = positions.reduce((s, p) => s + (p.pnl ?? 0), 0);
  const totalPnlPct = totalInvested > 0 ? (totalPnl / totalInvested) * 100 : 0;

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20 }}>
        Dashboard
      </h1>

      {/* NAV Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginBottom: 24 }}>
        <NavCard
          label="Portfolio Value"
          value={totalCurrent}
          change={totalPnl}
          changePct={totalPnlPct}
          icon="📊"
          color="blue"
        />
        <NavCard
          label="Day P&L"
          value={totalPnl}
          change={totalPnl}
          changePct={totalPnlPct}
          icon="💰"
          color={totalPnl >= 0 ? "green" : "red"}
        />
        <NavCard
          label="Invested"
          value={totalInvested}
          icon="🏦"
          color="amber"
        />
        <NavCard
          label="Open Positions"
          value={positions.length}
          icon="📈"
        />
      </div>

      {/* Charts: Equity Curve + Daily P&L */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 16, marginBottom: 24 }}>
        <EquityCurveChart />
        <PnlChart />
      </div>

      {/* Main grid: positions + order ticket */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 24, alignItems: "start" }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
            Open Positions
          </h2>
          <div className="card">
            <PositionTable />
          </div>
        </div>
        <OrderTicket />
      </div>

      {/* Quick stats */}
      <div style={{ display: "flex", gap: 16, marginTop: 24 }}>
        <div className="card" style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase" }}>
            Pending Orders
          </div>
          <div style={{ fontSize: 20, fontFamily: "var(--font-mono)", fontWeight: 600 }}>
            {orderData?.total ?? 0}
          </div>
        </div>
        <div className="card" style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase" }}>
            Real Positions
          </div>
          <div style={{ fontSize: 20, fontFamily: "var(--font-mono)", fontWeight: 600 }}>
            {positions.filter((p) => p.mode === "REAL").length}
          </div>
        </div>
        <div className="card" style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase" }}>
            Virtual Positions
          </div>
          <div style={{ fontSize: 20, fontFamily: "var(--font-mono)", fontWeight: 600 }}>
            {positions.filter((p) => p.mode === "VIRTUAL").length}
          </div>
        </div>
      </div>
    </div>
  );
}
